import { createFileRoute } from '@tanstack/react-router'
import { useQuery, skipToken } from '@tanstack/react-query'
import { useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { Spinner } from '@/components/ui/spinner'
import { UserIcon, BuildingIcon, BriefcaseIcon, EyeIcon, MailIcon, CalendarIcon } from 'lucide-react'
import { trpc } from '@/main'
import { formatDateLocale } from '@/lib/utils'
import { useTranslation } from '@/lib/locales'
import { useRootSearch } from '@/hooks/use-root-search'
import { SearchHeader } from './-components/api/search-header'
import { CompletionIndicator } from './-components/shared/completion-indicator'
import { Pagination } from './-components/shared/pagination'
import { SimpleListPending } from './-components/app-layout-skeleton'

export const Route = createFileRoute('/_app/account/api/$appId/applicants')({
  loaderDeps: ({ search }) => ({ p: search.p }),
  loader: ({ context, params, deps }) => {
    context.queryClient.prefetchQuery(
      trpc.apps.getApplicants.queryOptions({ appClientId: params.appId, search: { q: undefined, p: deps.p } }),
    )
  },
  component: RouteComponent,
  pendingComponent: SimpleListPending,
})

function RouteComponent() {
  const { appId } = Route.useParams()
  const { language } = useTranslation()
  const { t } = useLingui()
  const [query, setQuery] = useState<string | undefined>()
  const { p: page } = useRootSearch()

  const { data: applicants, isPending } = useQuery(
    trpc.apps.getApplicants.queryOptions(
      !appId
        ? skipToken
        : {
            appClientId: appId,
            search: { q: query, p: page },
          },
    ),
  )

  return (
    <div className="flex flex-col gap-4">
      <SearchHeader query={query ?? ''} onQueryChange={setQuery} count={applicants?.pagination.count} />

      <div className="flex flex-col gap-3" style={{ opacity: isPending ? 0.6 : undefined }}>
        {isPending && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}

        {!isPending && applicants?.list.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <UserIcon className="text-muted-foreground/50 size-12" />
            <p className="text-muted-foreground">{t`No applicants found`}</p>
          </div>
        )}

        {applicants?.list.map((elem) => {
          const { jobs, jobsAddedMe } = elem
          return (
            <div key={elem.id} className="bg-card rounded-lg border p-4">
              <div className="flex flex-col gap-3">
                {/* Header */}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{elem.name ?? t`Unnamed`}</h3>
                    <code className="text-muted-foreground bg-muted rounded px-1.5 py-0.5 text-xs">{elem.id}</code>
                    <CompletionIndicator
                      skills={elem.testPassed}
                      values={elem.testPassedValues}
                      traits={elem.testPassedPersonality}
                      size="md"
                    />
                  </div>

                  <div className="text-muted-foreground flex flex-col gap-1 text-xs sm:items-end">
                    {elem.created && (
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="size-3" />
                        {t`Created`}: {formatDateLocale(elem.created, language)}
                      </span>
                    )}
                    {elem.accessed && (
                      <span className="flex items-center gap-1">
                        <EyeIcon className="size-3" />
                        {t`Accessed`}: {formatDateLocale(elem.accessed, language)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Metadata */}
                <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
                  {elem.email && (
                    <span className="flex items-center gap-1">
                      <MailIcon className="size-3.5" />
                      {elem.email}
                    </span>
                  )}
                  {elem.company && (
                    <span className="flex items-center gap-1">
                      <BuildingIcon className="size-3.5" />
                      {elem.company}
                    </span>
                  )}
                </div>

                {/* Stats */}
                <div className="text-muted-foreground flex items-center gap-4 border-t pt-2 text-sm">
                  <span className="flex items-center gap-1">
                    <BriefcaseIcon className="size-3.5" />
                    {jobs === 1 ? t`${jobs} job` : t`${jobs} jobs`}
                  </span>
                  <span className="flex items-center gap-1">{t`${jobsAddedMe} added`}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <Pagination pagination={applicants?.pagination} />
    </div>
  )
}
